<script setup lang="ts">
/**
 * Glossary CRUD panel (ADR 0026 v2).
 *
 * Shared business-context definitions — same shape pattern as
 * AdminAssistants but for one short Markdown body per entry. The
 * chat path reads these from the DB on every turn (via a cache that
 * each save here invalidates), so a save lands on the next chat
 * answer without a backend restart.
 *
 * Per-assistant opt-in lives on AdminAssistants (settings.glossary_includes
 * — a multi-select of slugs from THIS page).
 */
import { vWrite } from "../admin-read-only";
import { computed, onMounted, ref } from "vue";
import {
  AdminError,
  type GlossaryEntryRow,
  deleteGlossaryEntry,
  listGlossary,
  upsertGlossaryEntry,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import HelpHint from "./HelpHint.vue";
import SkeletonRows from "./SkeletonRows.vue";
import SidePanel from "./SidePanel.vue";

const toast = useToast();
const { confirm } = useConfirm();

const props = defineProps<{ devUser: string }>();

const rows = ref<GlossaryEntryRow[]>([]);
const loading = ref(false);
const saving = ref(false);
const editing = ref<{ slug: string; body: string; isNew: boolean } | null>(
  null,
);

/** Drawer visibility, derived from `editing` so there is one source of
 * truth. Same treatment as the assistants tab — the form used to render
 * below the table, which put it off-screen once the library grew. */
const editorOpen = computed({
  get: () => editing.value !== null,
  set: (v: boolean) => {
    if (!v) cancelEdit();
  },
});

function _toastError(e: unknown, context: string): void {
  const msg = e instanceof AdminError ? e.message : String(e);
  toast.error(context, msg);
}

async function reload(): Promise<void> {
  loading.value = true;
  try {
    rows.value = await listGlossary(props.devUser);
  } catch (e) {
    _toastError(e, "Could not load glossary");
  } finally {
    loading.value = false;
  }
}

onMounted(reload);

function startNew(): void {
  editing.value = {
    slug: "",
    body: "## New term\n\nOne paragraph definition. Markdown — kept short (~200-300 words).",
    isNew: true,
  };
}

function startEdit(row: GlossaryEntryRow): void {
  editing.value = { slug: row.slug, body: row.body, isNew: false };
}

function cancelEdit(): void {
  editing.value = null;
}

async function save(): Promise<void> {
  if (!editing.value) return;
  const slug = editing.value.slug.trim();
  const body = editing.value.body;
  // Slug rules mirror the backend regex (Pydantic enforces it again).
  if (!slug || !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(slug)) {
    toast.error(
      "Invalid slug",
      "Slug must be kebab-case (a-z, 0-9, -); start and end with alnum.",
    );
    return;
  }
  if (!body.trim()) {
    toast.error("Empty body", "Glossary entry body cannot be empty.");
    return;
  }
  saving.value = true;
  try {
    await upsertGlossaryEntry({ slug, body }, props.devUser);
    const wasNew = editing.value.isNew;
    editing.value = null;
    await reload();
    toast.success(
      wasNew
        ? `Glossary entry ${slug} created`
        : `Glossary entry ${slug} saved`,
    );
  } catch (e) {
    _toastError(e, "Could not save glossary entry");
  } finally {
    saving.value = false;
  }
}

async function remove(row: GlossaryEntryRow): Promise<void> {
  const ok = await confirm({
    title: "Delete glossary entry?",
    message:
      `Delete "${row.slug}"? Assistants that still reference this slug ` +
      "in glossary_includes will warn-and-skip on each turn until you " +
      "remove the reference.",
    confirmLabel: "Delete",
    danger: true,
  });
  if (!ok) return;
  try {
    await deleteGlossaryEntry(row.slug, props.devUser);
    await reload();
    toast.success(`Glossary entry ${row.slug} deleted`);
  } catch (e) {
    _toastError(e, "Could not delete glossary entry");
  }
}

function preview(body: string): string {
  // Strip the leading "## X" heading + collapse to single-paragraph
  // preview for the list column. Bounded at 140 chars.
  const tail = body.replace(/^#{1,6}\s+[^\n]*\n+/, "").trim();
  const oneLine = tail.replace(/\s+/g, " ").trim();
  return oneLine.length > 140 ? oneLine.slice(0, 137) + "…" : oneLine;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return iso;
  return d.toLocaleString();
}
</script>

<template>
  <div class="admin-glossary">
    <header class="admin-glossary__header">
      <h2>
        Glossary
        <HelpHint wide align="start" label="About the glossary">
          Shared business-context definitions injected into the system prompt
          for every assistant that opts in via
          <code>settings.glossary_includes</code>. Edits land on the next chat
          turn — no backend restart.
        </HelpHint>
      </h2>
      <button v-write class="btn btn--primary" @click="startNew">+ New entry</button>
    </header>

    <SkeletonRows v-if="loading" :rows="3" />

    <p v-else-if="rows.length === 0" class="muted">
      No glossary entries yet. Click <strong>+ New entry</strong> to create one.
      On first boot the backend can also seed from
      <code>config/glossary/*.md</code>.
    </p>

    <table v-else class="admin-table">
      <thead>
        <tr>
          <th>Slug</th>
          <th>Title</th>
          <th>Preview</th>
          <th>Used by</th>
          <th>Last updated</th>
          <th class="admin-table__actions"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="r in rows" :key="r.slug">
          <td>
            <code>{{ r.slug }}</code>
          </td>
          <td>{{ r.title || "(no title)" }}</td>
          <td class="preview">{{ preview(r.body) }}</td>
          <td>
            <!-- `used_by` is documented as absent on PUT/DELETE responses
                 (see GlossaryEntryRow). Reading `.length` off undefined threw
                 inside v-for and blanked the whole tab, not just the cell. -->
            <span
              v-if="(r.used_by ?? []).length === 0"
              class="badge badge--orphan"
              title="No assistants reference this entry via settings.glossary_includes"
              >0 — unused</span
            >
            <span
              v-else
              class="badge badge--used"
              :title="`Used by: ${(r.used_by ?? []).join(', ')}`"
            >
              {{ (r.used_by ?? []).length }}
              {{ (r.used_by ?? []).length === 1 ? "assistant" : "assistants" }}
            </span>
          </td>
          <td class="muted">{{ fmtDate(r.updated_at) }}</td>
          <td class="admin-table__actions">
            <button class="btn" @click="startEdit(r)">Edit</button>
            <button v-write class="btn btn--ghost" @click="remove(r)">Delete</button>
          </td>
        </tr>
      </tbody>
    </table>

    <SidePanel
      v-model="editorOpen"
      :title="editing?.isNew ? 'New glossary entry' : (editing?.slug ?? '')"
      :subtitle="editing?.isNew ? '' : 'glossary entry'"
      width="min(40rem, 92vw)"
    >
      <div v-if="editing" class="editor">
        <div class="editor__row">
          <label>
            <span class="editor__label">Slug</span>
            <input
              v-model="editing.slug"
              type="text"
              :disabled="!editing.isNew"
              placeholder="customer"
              class="editor__input"
            />
            <span class="hint">
              kebab-case. Referenced from
              <code>settings.glossary_includes</code> on assistants.
            </span>
          </label>
        </div>

        <div class="editor__row">
          <label>
            <span class="editor__label">Body (Markdown)</span>
            <textarea
              v-model="editing.body"
              rows="14"
              class="editor__textarea"
              placeholder="## Customer&#10;&#10;A legal counterparty of a Risma contract…"
            />
            <span class="hint">
              Title is auto-derived from the first heading. Keep entries short
              (~200–300 words) — they ride on every chat turn for every
              assistant that includes them.
            </span>
          </label>
        </div>
      </div>
      <template #actions>
        <button class="btn" :disabled="saving" @click="cancelEdit">
          Cancel
        </button>
        <button v-write class="btn btn--primary" :disabled="saving" @click="save">
          {{ saving ? "Saving…" : "Save" }}
        </button>
      </template>
    </SidePanel>
  </div>
</template>

<style scoped>
.admin-glossary {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.admin-glossary__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
}
@media (max-width: 640px) {
  .admin-glossary__header .btn {
    flex: 0 0 auto;
    white-space: nowrap;
  }
}
.admin-glossary__header h2 {
  margin: 0;
}
.admin-table__actions {
  text-align: right;
  white-space: nowrap;
}
.admin-table__actions .btn {
  margin-left: var(--space-1);
}
.preview {
  color: var(--color-muted);
  font-size: 0.9rem;
  max-width: 60ch;
}
@media (max-width: 720px) {
  /* The table scrolls sideways on a phone, so a 60ch no-wrap preview puts
     the actions column a long swipe away. Truncate harder here — the full
     body is one tap away in the editor. (The ellipsis itself comes from the
     global .admin-table rule, which clips capped cells at this breakpoint.) */
  .preview {
    max-width: 22ch;
  }
}
.badge {
  display: inline-block;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: help;
  border: 1px solid var(--color-border);
}
.badge--used {
  border-color: var(--color-accent);
  background: color-mix(in srgb, var(--color-accent) 14%, transparent);
  color: var(--color-accent);
}
.badge--orphan {
  color: var(--color-muted);
}
.btn {
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  padding: 0.4rem 0.9rem;
  border-radius: var(--radius-sm);
  font-size: 0.9rem;
  cursor: pointer;
  color: var(--color-ink);
}
.btn:hover {
  border-color: var(--color-ink);
}
.btn--primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}
.btn--primary:hover {
  background: var(--color-accent-hover);
  border-color: var(--color-accent-hover);
}
.btn--danger {
  color: var(--color-error);
  border-color: var(--color-error);
}
.btn--danger:hover {
  background: var(--color-error);
  color: white;
}
.btn--ghost {
  background: transparent;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.editor {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.editor__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.editor__header h3 {
  margin: 0;
}
.editor__actions {
  display: flex;
  gap: 0.5rem;
}
.editor__row label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.editor__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.editor__input,
.editor__textarea {
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 0.95rem;
  background: var(--color-bg);
  color: var(--color-ink);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 0.5rem 0.7rem;
  width: 100%;
}
.editor__textarea {
  resize: vertical;
  line-height: 1.5;
  min-height: 14em;
}
.editor__input:focus,
.editor__textarea:focus {
  outline: none;
  border-color: var(--color-accent);
}
.editor__input:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.hint {
  color: var(--color-muted);
  font-size: 0.85rem;
}
code {
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 0.9em;
  background: var(--color-surface-alt);
  padding: 0.1em 0.35em;
  border-radius: 4px;
}
.muted {
  color: var(--color-muted);
}
</style>
